<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue';
import { useCmdK } from '@/composables/useCmdK';
import { useNotifications } from '@/composables/useNotifications';
import IconMenu from '@/components/icons/IconMenu.vue';
import IconSearch from '@/components/icons/IconSearch.vue';
import IconBell from '@/components/icons/IconBell.vue';
import IconHelp from '@/components/icons/IconHelp.vue';
import NotificationsDropdown from '@/components/shell/NotificationsDropdown.vue';

defineEmits<{ (e: 'toggle-drawer'): void }>();

const searchRef = useTemplateRef<HTMLInputElement>('search-input');
useCmdK(searchRef);

const isMac = computed(
  () =>
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform ?? ''),
);
const shortcutHint = computed(() => (isMac.value ? '⌘K' : 'Ctrl K'));

const searchQuery = ref('');

// Notifications
const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
const notificationsOpen = ref(false);

function toggleNotifications() {
  notificationsOpen.value = !notificationsOpen.value;
}
function closeNotifications() {
  notificationsOpen.value = false;
}
function handleMarkRead(id: string) {
  void markRead(id);
}
function handleMarkAllRead() {
  void markAllRead();
}
</script>

<template>
  <header
    class="sticky top-0 z-20 bg-surface border-b border-border h-14 flex items-center px-4 lg:px-8 gap-4"
  >
    <button
      type="button"
      class="lg:hidden w-9 h-9 rounded-md grid place-items-center hover:bg-canvas-soft text-fg"
      data-testid="drawer-toggle"
      aria-label="Open navigation"
      @click="$emit('toggle-drawer')"
    >
      <IconMenu class="w-5 h-5" />
    </button>

    <div class="relative flex-1 max-w-[460px]">
      <IconSearch
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none"
      />
      <input
        ref="search-input"
        v-model="searchQuery"
        type="search"
        placeholder="Search issues, addresses, residents…"
        class="w-full h-[38px] pl-[38px] pr-2 bg-canvas-soft rounded-md border border-transparent focus:outline-none focus:border-navy-500 focus:bg-surface focus:ring-4 focus:ring-navy-100 transition"
        data-testid="topbar-search"
      />
      <kbd
        class="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[11px] bg-surface border border-border rounded-xs px-1.5 py-0.5 text-muted pointer-events-none"
      >{{ shortcutHint }}</kbd>
    </div>

    <div class="ml-auto flex items-center gap-2">
      <!-- Bell button + dropdown anchor -->
      <div class="relative">
        <button
          type="button"
          class="w-9 h-9 rounded-md grid place-items-center hover:bg-canvas-soft text-fg-soft relative"
          aria-label="Notifications"
          data-testid="bell-button"
          @click="toggleNotifications"
        >
          <IconBell class="w-[18px] h-[18px]" />
          <!-- Unread dot -->
          <span
            v-if="unreadCount > 0"
            class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-600 border-2 border-surface"
            data-testid="bell-dot"
            aria-hidden="true"
          />
        </button>

        <!-- Dropdown -->
        <NotificationsDropdown
          v-if="notificationsOpen"
          :notifications="notifications"
          :unread-count="unreadCount"
          @close="closeNotifications"
          @mark-read="handleMarkRead"
          @mark-all-read="handleMarkAllRead"
        />
      </div>
      <button
        type="button"
        class="w-9 h-9 rounded-md grid place-items-center hover:bg-canvas-soft text-fg-soft"
        aria-label="Help"
      >
        <IconHelp class="w-[18px] h-[18px]" />
      </button>
    </div>
  </header>
</template>
